/**
 * Profit Trend Chart Component
 *
 * Multi-line chart showing Revenue, COGS, OpEx, and Net Profit over time.
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { ProfitTrendData } from '../../../types/financialInsights';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

interface ProfitTrendChartProps {
  data: ProfitTrendData | undefined;
  loading: boolean;
}

export const ProfitTrendChart: React.FC<ProfitTrendChartProps> = ({
  data,
  loading,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="rectangular" width="100%" height={350} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.revenue.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ height: 350 }}
          >
            <Typography variant="h6" color="text.secondary">
              No Profit Data Available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const chartData = {
    labels: data.revenue.map(d => d.date),
    datasets: [
      {
        label: 'Revenue',
        data: data.revenue.map(d => d.value),
        borderColor: '#4caf50',
        backgroundColor: '#4caf5033',
        tension: 0.4,
      },
      {
        label: 'COGS',
        data: data.cogs.map(d => d.value),
        borderColor: '#ff9800',
        backgroundColor: '#ff980033',
        tension: 0.4,
      },
      {
        label: 'OpEx',
        data: data.opex.map(d => d.value),
        borderColor: '#f44336',
        backgroundColor: '#f4433633',
        tension: 0.4,
      },
      {
        label: 'Net Profit',
        data: data.net_profit.map(d => d.value),
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}33`,
        tension: 0.4,
        borderWidth: 3,
      },
    ],
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Profit & Loss Trend
        </Typography>
        <Box sx={{ height: 350 }}>
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfitTrendChart;
